<template>
  <div class="dashboard">
    <!-- Welcome Section -->
    <div class="welcome-section">
      <div class="welcome-content">
        <h2>👋 Bienvenue, {{ user?.prenom }} {{ user?.nom }} ! 🎉</h2>
        <p>📊 Voici un aperçu de votre plateforme d'administration ✨</p>
      </div>
      <div class="welcome-stats">
        <div class="current-time">
          <i class="fas fa-clock"></i>
          {{ currentTime }}
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card" v-for="stat in statsCards" :key="stat.key">
        <div class="stat-icon" :style="{ backgroundColor: stat.color }">
          <i :class="stat.icon"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats[stat.key] || 0 }}</div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
        <div class="stat-trend" :class="stat.trend">
          <i :class="stat.trendIcon"></i>
          {{ stat.trendValue }}
        </div>
      </div>
    </div>

    <!-- Charts and Activity -->
    <div class="dashboard-grid">
      <!-- Users by Role Chart -->
      <div class="dashboard-card">
        <div class="card-header">
          <h3>👥 Répartition par Rôles</h3>
          <div class="card-actions">
            <button class="btn btn-sm btn-secondary">
              <i class="fas fa-download"></i>
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="chart-container">
            <canvas ref="roleChart"></canvas>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="dashboard-card">
        <div class="card-header">
          <h3>⚡ Activité Récente</h3>
          <router-link to="/users" class="btn btn-sm btn-primary">
            Voir tout
          </router-link>
        </div>
        <div class="card-body">
          <div class="activity-list">
            <div 
              v-for="activity in activiteRecente" 
              :key="activity._id"
              class="activity-item"
            >
              <div class="activity-avatar">
                <i class="fas fa-user"></i>
              </div>
              <div class="activity-content">
                <div class="activity-text">
                  <strong>{{ activity.prenom }} {{ activity.nom }}</strong>
                  s'est connecté
                </div>
                <div class="activity-time">
                  {{ formatTime(activity.dernierConnexion) }}
                </div>
              </div>
              <div class="activity-role">
                <span class="badge" :class="getRoleBadgeClass(activity.role)">
                  {{ activity.role }}
                </span>
              </div>
            </div>
            
            <div v-if="!activiteRecente.length" class="no-activity">
              <i class="fas fa-inbox"></i>
              <p>😴 Aucune activité récente</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Monthly Growth Chart -->
      <div class="dashboard-card full-width">
        <div class="card-header">
          <h3>📈 Évolution des Utilisateurs</h3>
          <div class="chart-filters">
            <select v-model="chartPeriod" class="form-control">
              <option value="6">6 derniers mois</option>
              <option value="12">12 derniers mois</option>
            </select>
          </div>
        </div>
        <div class="card-body">
          <div class="chart-container">
            <canvas ref="growthChart"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions" v-if="isAdmin">
      <h3>🚀 Actions Rapides</h3>
      <div class="actions-grid">
        <button 
          class="action-card"
          @click="showAddUserModal = true"
        >
          <div class="action-icon">
            <i class="fas fa-user-plus"></i>
          </div>
          <div class="action-text">
            <div class="action-title">➕ Ajouter Utilisateur</div>
            <div class="action-subtitle">Créer un nouveau compte</div>
          </div>
        </button>

        <router-link to="/users" class="action-card">
          <div class="action-icon">
            <i class="fas fa-users-cog"></i>
          </div>
          <div class="action-text">
            <div class="action-title">⚙️ Gérer Utilisateurs</div>
            <div class="action-subtitle">Modifier les comptes</div>
          </div>
        </router-link>

        <button class="action-card" @click="exportData">
          <div class="action-icon">
            <i class="fas fa-download"></i>
          </div>
          <div class="action-text">
            <div class="action-title">📥 Exporter Données</div>
            <div class="action-subtitle">Télécharger le rapport</div>
          </div>
        </button>
      </div>
    </div>

    <!-- Add User Modal -->
    <AddUserModal 
      v-if="showAddUserModal"
      @close="showAddUserModal = false"
      @user-added="handleUserAdded"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import { Chart, registerables } from 'chart.js'
import AddUserModal from '../components/AddUserModal.vue'

Chart.register(...registerables)

export default {
  name: 'Dashboard',
  components: {
    AddUserModal
  },
  setup() {
    const store = useStore()
    const toast = useToast()

    const roleChart = ref(null)
    const growthChart = ref(null)
    const currentTime = ref('')
    const chartPeriod = ref('6')
    const showAddUserModal = ref(false)

    const user = computed(() => store.getters['auth/user'])
    const isAdmin = computed(() => store.getters['auth/isAdmin'])
    const stats = ref({
      interventionsPlanifiees: 0,
      interventionsEnCours: 0,
      interventionsTerminees: 0,
      totalEncaissements: 0
    })
    const statistiquesParRole = computed(() => store.getters['dashboard/statistiquesParRole'])
    const activiteRecente = computed(() => store.getters['dashboard/activiteRecente'])
    const utilisateursParMois = computed(() => store.getters['dashboard/utilisateursParMois'])

    const statsCards = [
      {
        key: 'interventionsPlanifiees',
        label: 'Planifiées',
        icon: 'fas fa-calendar-alt',
        color: 'var(--primary-blue)',
        trend: 'positive',
        trendIcon: 'fas fa-arrow-up',
        trendValue: '+12%'
      },
      {
        key: 'interventionsEnCours',
        label: 'En Cours',
        icon: 'fas fa-tools',
        color: 'var(--accent-orange)',
        trend: 'positive',
        trendIcon: 'fas fa-arrow-up',
        trendValue: '+8%'
      },
      {
        key: 'interventionsTerminees',
        label: 'Terminées',
        icon: 'fas fa-check-circle',
        color: 'var(--success)',
        trend: 'positive',
        trendIcon: 'fas fa-arrow-up',
        trendValue: '+15%'
      },
      {
        key: 'totalEncaissements',
        label: 'Encaissements (DT)',
        icon: 'fas fa-money-bill-wave',
        color: '#10b981',
        trend: 'positive',
        trendIcon: 'fas fa-arrow-up',
        trendValue: '+20%'
      }
    ]

    const updateTime = () => {
      const now = new Date()
      currentTime.value = now.toLocaleString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatTime = (dateString) => {
      if (!dateString) return 'Jamais'
      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)
      
      if (minutes < 1) return 'À l\'instant'
      if (minutes < 60) return `Il y a ${minutes} min`
      if (hours < 24) return `Il y a ${hours}h`
      if (days < 7) return `Il y a ${days} jour(s)`
      
      return date.toLocaleDateString('fr-FR')
    }

    const getRoleBadgeClass = (role) => {
      const classes = {
        'Admin': 'badge-primary',
        'Responsable Planning': 'badge-info',
        'Commercial': 'badge-success',
        'Magasinier': 'badge-warning',
        'Technicien': 'badge-secondary'
      }
      return classes[role] || 'badge-secondary'
    }

    const createRoleChart = () => {
      if (!roleChart.value || !statistiquesParRole.value.length) return

      const ctx = roleChart.value.getContext('2d')
      
      new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: statistiquesParRole.value.map(stat => stat._id),
          datasets: [{
            data: statistiquesParRole.value.map(stat => stat.count),
            backgroundColor: [
              '#FF6B6B', // Rouge corail pour Admin
              '#4ECDC4', // Turquoise pour Technicien
              '#45B7D1', // Bleu ciel pour Responsable Planning
              '#96CEB4', // Vert menthe pour Commercial
              '#FFEAA7', // Jaune doux pour Magasinier
              '#DDA0DD', // Violet clair pour autres rôles
              '#FFB347', // Orange pêche
              '#87CEEB'  // Bleu ciel clair
            ],
            borderColor: [
              '#FF5252', // Bordure rouge plus foncée
              '#26A69A', // Bordure turquoise plus foncée
              '#2196F3', // Bordure bleue plus foncée
              '#66BB6A', // Bordure verte plus foncée
              '#FFCA28', // Bordure jaune plus foncée
              '#BA68C8', // Bordure violette plus foncée
              '#FF9800', // Bordure orange plus foncée
              '#42A5F5'  // Bordure bleue plus foncée
            ],
            borderWidth: 2,
            hoverBackgroundColor: [
              '#FF5252', // Hover rouge
              '#26A69A', // Hover turquoise
              '#2196F3', // Hover bleu
              '#66BB6A', // Hover vert
              '#FFCA28', // Hover jaune
              '#BA68C8', // Hover violet
              '#FF9800', // Hover orange
              '#42A5F5'  // Hover bleu
            ],
            hoverBorderWidth: 3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%', // Trou au centre plus grand pour un look moderne
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                padding: 20,
                usePointStyle: true,
                font: {
                  size: 12,
                  weight: '500'
                },
                color: '#374151'
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: '#fff',
              borderWidth: 1,
              cornerRadius: 8,
              displayColors: true,
              callbacks: {
                label: function(context) {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((context.parsed * 100) / total).toFixed(1);
                  return `${context.label}: ${context.parsed} (${percentage}%)`;
                }
              }
            }
          },
          animation: {
            animateRotate: true,
            animateScale: true,
            duration: 1000,
            easing: 'easeOutQuart'
          },
          interaction: {
            intersect: false,
            mode: 'nearest'
          }
        },
        plugins: [{
          id: 'centerText',
          beforeDraw: function(chart) {
            const ctx = chart.ctx;
            const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
            const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;
            
            const total = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
            
            ctx.save();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            
            // Nombre total
            ctx.font = 'bold 24px Inter, sans-serif';
            ctx.fillStyle = '#1F2937';
            ctx.fillText(total, centerX, centerY - 8);
            
            // Label
            ctx.font = '12px Inter, sans-serif';
            ctx.fillStyle = '#6B7280';
            ctx.fillText('Utilisateurs', centerX, centerY + 12);
            
            ctx.restore();
          }
        }]
      })
    }

    const createGrowthChart = () => {
      if (!growthChart.value || !utilisateursParMois.value.length) return

      const ctx = growthChart.value.getContext('2d')
      
      const labels = utilisateursParMois.value.map(data => {
        const date = new Date(data._id.year, data._id.month - 1)
        return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
      })

      new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Nouveaux utilisateurs',
            data: utilisateursParMois.value.map(data => data.count),
            borderColor: 'var(--primary-blue)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            tension: 0.4,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            },
            x: {
              grid: {
                display: false
              }
            }
          }
        }
      })
    }

    const initCharts = () => {
      createRoleChart()
      createGrowthChart()
    }

    const loadDashboardData = async () => {
      try {
        const token = localStorage.getItem('token')
        
        // Charger les interventions
        const rdvResponse = await fetch('/api/rdv?limit=1000', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (rdvResponse.ok) {
          const rdvData = await rdvResponse.json()
          const interventions = rdvData.rdvs || []
          
          // Compter par statut
          stats.value.interventionsPlanifiees = interventions.filter(i => ['Planifié', 'Arrivé'].includes(i.statut)).length
          stats.value.interventionsEnCours = interventions.filter(i => ['En cours', 'Pause'].includes(i.statut)).length
          stats.value.interventionsTerminees = interventions.filter(i => i.statut === 'Terminé').length
          
          // Calculer le total des encaissements
          stats.value.totalEncaissements = interventions
            .filter(i => i.encaissement && i.encaissement.montant)
            .reduce((total, i) => total + i.encaissement.montant, 0)
        }
        
        await store.dispatch('dashboard/fetchDashboardData')
        
        setTimeout(() => {
          initCharts()
        }, 100)
      } catch (error) {
        console.error('Erreur chargement dashboard:', error)
        // Erreur silencieuse - ne pas afficher de toast pour ne pas perturber l'utilisateur
      }
    }

    const handleUserAdded = () => {
      showAddUserModal.value = false
      loadDashboardData()
      toast.success('Utilisateur ajouté avec succès')
    }

    const exportData = () => {
      toast.info('Fonctionnalité d\'export en cours de développement')
    }

    onMounted(() => {
      updateTime()
      const timeInterval = setInterval(updateTime, 60000)
      
      loadDashboardData()

      onUnmounted(() => {
        clearInterval(timeInterval)
      })
    })

    return {
      user,
      isAdmin,
      stats,
      statistiquesParRole,
      activiteRecente,
      statsCards,
      currentTime,
      chartPeriod,
      showAddUserModal,
      roleChart,
      growthChart,
      formatTime,
      getRoleBadgeClass,
      handleUserAdded,
      exportData
    }
  }
}
</script>

<style scoped>
.dashboard {
  max-width: 1400px;
  margin: 0 auto;
}

.welcome-section {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: var(--white);
  padding: 2rem;
  border-radius: 1rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.welcome-content h2 {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.welcome-content p {
  opacity: 0.9;
  margin: 0;
}

.current-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  opacity: 0.9;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: var(--white);
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  font-size: 1.5rem;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--dark-gray);
  line-height: 1;
}

.stat-label {
  color: var(--medium-gray);
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.stat-trend {
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.stat-trend.positive {
  color: var(--success);
}

.stat-trend.negative {
  color: var(--error);
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.dashboard-card {
  background: var(--white);
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.dashboard-card.full-width {
  grid-column: 1 / -1;
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--light-gray);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--dark-gray);
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.chart-filters select {
  min-width: 150px;
}

.card-body {
  padding: 1.5rem;
}

.chart-container {
  height: 300px;
  position: relative;
}

.activity-list {
  max-height: 400px;
  overflow-y: auto;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--light-gray);
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: var(--white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.activity-content {
  flex: 1;
}

.activity-text {
  font-size: 0.875rem;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
}

.activity-time {
  font-size: 0.75rem;
  color: var(--medium-gray);
}

.no-activity {
  text-align: center;
  padding: 2rem;
  color: var(--medium-gray);
}

.no-activity i {
  font-size: 2rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.quick-actions {
  margin-bottom: 2rem;
}

.quick-actions h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 1rem;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.action-card {
  background: var(--white);
  border: 2px solid var(--light-gray);
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.action-card:hover {
  border-color: var(--primary-blue);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.15);
}

.action-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: var(--white);
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.action-text {
  flex: 1;
}

.action-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
  line-height: 1.4;
  letter-spacing: 0.02em;
}

.action-subtitle {
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--medium-gray);
  line-height: 1.3;
  letter-spacing: 0.01em;
}

/* Responsive */
@media (max-width: 1024px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .welcome-section {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .actions-grid {
    grid-template-columns: 1fr;
  }
}
</style>
